// appCommentsService.ts
import {
  type AppCommentRecord,
  createComment as createCommentApi,
  listCommentsByThread,
} from '../api/commentsApi';
import {
  type CommentThreadRecord,
  getThreadByResource as fetchThreadByResource,
  getThreadLastAt as fetchThreadLastAt,
} from '../api/commentThreadsApi';
import { markThreadRead as markThreadReadRpc } from '../api/threadReadsApi';

// mantenha igual ao enum do banco
export type RecursoTipo =
  | "tarefa"
  | "checklist_item"
  | "cronograma_etapa"
  | "cronograma_subetapa"
  | "obra"
  | "diario_relatorio"
  | "compras_requisicao"
  | "requisicao_compra"
  | "servico"
  | "diario_alteracoes_projeto";

export interface AppComment {
  id: string;
  thread_id: string;
  parent_id: string | null;
  conteudo: string;
  tipo: string;
  arquivo_url: string | null;
  arquivo_nome: string | null;
  mencoes: string[] | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  // se você já tem join de usuário no frontend, dá pra anexar aqui
}

export interface AppCommentThread {
  id: string;
  obra_id: string | null;
  recurso: RecursoTipo;
  recurso_id: string;
  created_at: string;
  created_by: string | null;
  last_at: string | null;
}

export const appCommentsService = {
  /**
   * Busca a thread pelo recurso. Útil pra montar a tela antes de listar os comentários.
   */
  async getThreadByResource(recurso: RecursoTipo, recursoId: string) {
    const data = await fetchThreadByResource(recurso, recursoId);
    return data ? mapThreadRecord(data) : null;
  },

  /**
   * Lista comentários de uma thread.
   */
  async listComments(threadId: string) {
    const data = await listCommentsByThread(threadId);
    return data.map(mapCommentRecord);
  },

  /**
   * Cria comentário usando a RPC nova.
   * Você pode passar thread OU (recurso,recursoId).
   */
  async createComment(params: {
    conteudo: string;
    threadId?: string;
    recurso?: RecursoTipo;
    recursoId?: string;
    obraId?: string;
    tipo?: string;
    arquivoUrl?: string | null;
    arquivoNome?: string | null;
    mencoes?: string[] | null;
    createdBy?: string | null;
    userId?: string | null;
  }) {
    const {
      conteudo,
      threadId,
      recurso,
      recursoId,
      obraId,
      tipo = "texto",
      arquivoUrl = null,
      arquivoNome = null,
      mencoes = null,
      createdBy = null,
      userId = null,
    } = params;

    if (!threadId && (!recurso || !recursoId)) {
      throw new Error('createComment requer threadId ou (recurso, recursoId)');
    }

    if (!threadId && !obraId) {
      throw new Error('createComment requer obraId quando usado com recurso/recursoId');
    }

    const basePayload = {
      conteudo,
      tipo,
      arquivoUrl,
      arquivoNome,
      mencoes,
      createdBy,
      userId,
    };

    const created = await createCommentApi(
      threadId
        ? {
            threadId,
            ...basePayload,
            ...(obraId ? { obraId } : {}),
          }
        : {
            recurso: recurso!,
            recursoId: recursoId!,
            obraId: obraId!,
            ...basePayload,
          },
    );

    return created.id;
  },

  /**
   * Marca a thread como lida para o usuário atual.
   */
  async markThreadRead(threadId: string) {
    await markThreadReadRpc(threadId);
  },

  /**
   * Busca o last_at de uma thread, protegendo contra id vazio/undefined.
   */
  async getThreadLastAt(threadId: string | undefined | null): Promise<string | null> {
    if (!threadId) {
      // nada pra buscar, evita /id=eq.undefined
      return null;
    }

    try {
      return await fetchThreadLastAt(threadId);
    } catch (error) {
      console.warn('getThreadLastAt error', error);
      return null;
    }
  },
};

function mapCommentRecord(row: AppCommentRecord): AppComment {
  return {
    id: row.id,
    thread_id: row.thread_id,
    parent_id: row.parent_id,
    conteudo: row.conteudo ?? '',
    tipo: row.tipo ?? 'texto',
    arquivo_url: row.arquivo_url,
    arquivo_nome: row.arquivo_nome,
    mencoes: row.mencoes,
    created_at: row.created_at,
    updated_at: row.updated_at ?? row.created_at,
    created_by: row.created_by,
  };
}

function mapThreadRecord(row: CommentThreadRecord): AppCommentThread {
  return {
    id: row.id,
    obra_id: row.obra_id,
    recurso: row.recurso as RecursoTipo,
    recurso_id: row.recurso_id,
    created_at: row.created_at,
    created_by: row.created_by,
    last_at: row.last_at,
  };
}
